import { ListTodo, Plus } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { DeleteIconButton } from "./DeleteIconButton";
import { SectionTitle } from "./SectionTitle";

export interface DraftTask {
  key: number;
  text: string;
  /** `YYYY-MM-DDTHH:mm` from a datetime-local input, or empty */
  due: string;
}

/** Tasks typed while creating a card, saved together with the card. */
export const DraftTasksSection = ({
  items,
  onChange,
}: {
  items: DraftTask[];
  onChange: (items: DraftTask[]) => void;
}) => {
  const translate = useTranslate();
  const [isAdding, setIsAdding] = useState(false);
  const [text, setText] = useState("");
  const [due, setDue] = useState("");

  const add = () => {
    if (!text.trim()) return;
    onChange([...items, { key: Date.now(), text: text.trim(), due }]);
    setText("");
    setDue("");
    setIsAdding(false);
  };

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle icon={<ListTodo />}>
        {translate("crm.operations.tasks.title")}
      </SectionTitle>
      {items.map((item) => (
        <div
          key={item.key}
          className="flex items-center gap-2 rounded-md border px-3 py-1.5"
        >
          <span className="min-w-0 flex-1 truncate text-sm">{item.text}</span>
          {item.due && (
            <span className="text-xs text-muted-foreground">
              {item.due.replace("T", " ")}
            </span>
          )}
          <DeleteIconButton
            label={translate("crm.operations.tasks.delete")}
            onConfirm={() =>
              onChange(items.filter((candidate) => candidate.key !== item.key))
            }
          />
        </div>
      ))}
      {isAdding ? (
        <div className="flex flex-col gap-2 rounded-md border p-3">
          <Input
            autoFocus
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add();
              }
            }}
            placeholder={translate("crm.operations.tasks.fields.title")}
            aria-label={translate("crm.operations.tasks.fields.title")}
          />
          <Input
            type="datetime-local"
            value={due}
            onChange={(event) => setDue(event.target.value)}
            aria-label={translate("crm.operations.tasks.fields.due_date")}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!text.trim()}
              onClick={add}
            >
              {translate("ra.action.add")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setIsAdding(true)}
        >
          <Plus />
          {translate("crm.operations.tasks.add")}
        </Button>
      )}
    </section>
  );
};
